import './index.css'
import { ReactNode, useEffect, useState } from "react"
import DataTable from "../common/DataTable"
import { columns, handleDoubleClickRow } from "./scheme"
import {
  getEntity, filterEntities, createEntity,
  editEntity, deleteEntity
} from "../../services/entities.service"
import ActionModal from "../common/ActionModal"
import { DataEntity, EntityFormModel } from "../../models/entity.model"
import EntityForm from "./EntityForm"
import ConfirmModal from "../common/ConfirmModal"
import { SubmitHandler } from "react-hook-form"
import { useProject } from "../../context/ProjectContext"
import { APIResponseModel } from "../../models/api-call"
import { useNotification } from '../../context/NotificationContext'


interface EntitiesListingProps {
  setBlockScreen: (p: any) => void
  setOpenActionModal: (p: any) => void
  openActionModal: boolean
  setAction: React.Dispatch<React.SetStateAction<any>>
  action: 'create' | 'edit' | 'view'
  canEdit: boolean
  project_id: string
  entity_type: string
  entity_name: string
}

export default function EntitiesListing({ setBlockScreen, setOpenActionModal, openActionModal,
  action, setAction, canEdit, entity_type, entity_name }: EntitiesListingProps) {
  
  const { selectedProject } = useProject()
  const { showNotification } = useNotification();

  const [loadingList, setLoadingList] = useState<boolean>(false)
  const [reloadList, setReloadList] = useState(false)

  const [entityCode, setEntityCode] = useState<string>('')
  const clearSelection = () => setEntityCode('')
  const [openConfirmModal, setOpenConfirmModal] = useState(false)

  const [fieldErrors, setFieldErrors] = useState<string[]>([])

  const initialForm: EntityFormModel = {
    name: '',
    description: '',
    created_date: '',
    project_id: '',
    project_name: '',
    editable: ''
  }

  const [entityForm, setEntityForm] = useState<EntityFormModel>(initialForm)
  const [entitiesList, setEntitiesList] = useState<DataEntity[]>([])

  const errorFields = () => {
    let errors: string[] = []
    if (entityForm.name.length === 0) errors.push('name')
    setFieldErrors(errors)

    return errors.length > 0
  }

  const handleCloseDetail = () => {
    setOpenActionModal(false)
    clearSelection()
    setAction('view')
    setEntityForm(initialForm)
    setFieldErrors([])
  }

  const fetchDataTable = () => {
    setLoadingList(true)
    Promise.all([filterEntities(selectedProject ? selectedProject : 'ALL', entity_type, entity_name, { 'project_id': selectedProject })
    ]).then(function (result: APIResponseModel[]) {
      setEntitiesList(result[0].data)
      setLoadingList(false)
    }).catch((error) => {
      showNotification(<span>Error obteniendo la lista de entidades</span>, 'error')
      setLoadingList(false)
    });
  };

  useEffect(fetchDataTable, [reloadList, selectedProject, entity_name])

  const fetchEntity = () => {
    if (entityCode) {
      setBlockScreen(true)
      Promise.all([getEntity(selectedProject ? selectedProject : 'ALL', entity_type, entity_name, entityCode)
      ]).then(function (result: APIResponseModel[]) {
        setEntityForm(result[0].data)
        setOpenActionModal(true)
        setBlockScreen(false)
      }).catch((error) => {
        showNotification(<span>
          Error: {error.response.data.message ? error.response.data.message : error.response.data}
          </span>, 'error')
        setBlockScreen(false)
      });
    }
  }

  useEffect(fetchEntity, [entityCode])

  const onSubmitCreate: SubmitHandler<EntityFormModel> = () => {
    if (!errorFields()) {
      setBlockScreen(true)
      Promise.all([createEntity(selectedProject ? selectedProject : 'ALL', entity_type, entity_name, entityForm, selectedProject)
      ]).then(function (result: APIResponseModel[]) {
        setTimeout(() => {
          showNotification(<span>Se ha creado la Entidad <i>{entityForm.name}</i></span>)
          handleCloseDetail()
          setReloadList(!reloadList)
        }, 1000)
        setBlockScreen(false)
      }).catch((error) => {
        showNotification(<span>{error.response.data.message}</span>, 'error')
        setBlockScreen(false);
      });
    }
  }

  const onSubmitEdit: SubmitHandler<EntityFormModel> = () => {
    if (!errorFields()) {
      setBlockScreen(true)
      Promise.all([editEntity(selectedProject ? selectedProject : 'ALL', entity_type, entity_name, entityCode, entityForm)
      ]).then(function (result: APIResponseModel[]) {
        setTimeout(() => {
          showNotification(<span>Se ha editado la Entidad <i>{entityForm.name}</i></span>)
          handleCloseDetail()
          setReloadList(!reloadList)
        }, 1000)
        setBlockScreen(false)
      }).catch((error) => {
        showNotification(<span>{error.response.data.message}</span>, 'error')
        setBlockScreen(false);
      });
    }
  }

  const onSubmitDelete = () => {
    setBlockScreen(true)
    Promise.all([deleteEntity(selectedProject ? selectedProject : 'ALL', entity_type, entity_name, entityCode)
    ]).then(function (result: APIResponseModel[]) {
      setTimeout(() => {
        showNotification(<span>Se ha removido la entidad <i>{entityForm.name}</i></span>)
        handleCloseDetail()
        setOpenConfirmModal(false)
        setReloadList(!reloadList)
      }, 1000)
      setBlockScreen(false)
    }).catch((error) => {
      showNotification(<span>{error.response.data.message}</span>, 'error')
      setOpenConfirmModal(false)
      setReloadList(!reloadList)
      setBlockScreen(false);
    });
  }

  const handleRemoveEntity = () => {
    setOpenConfirmModal(true)
  }

  const handleCloseConfirmRemove = () => {
    setOpenConfirmModal(false)
  }

  const handleDoubleClickEntity = (event: any) => {
    handleDoubleClickRow(event, setEntityCode)
  }

  return (
    <>
      <ActionModal
        title={action === 'create' ? 'Nueva Entidad' : 'Ver Entidad'}
        openModal={openActionModal}
        handleCloseModal={handleCloseDetail}
        mode={action}
        setMode={setAction}
        handleCreate={onSubmitCreate}
        handleEdit={onSubmitEdit}
        handleRemove={handleRemoveEntity}
        canEdit={canEdit && (entityForm.editable === 'true')}
        children={<EntityForm action={action}
          entityForm={entityForm}
          setEntityForm={setEntityForm}
          errors={fieldErrors}
          editable={entityForm.editable === 'true'}
        />}
      />

      <ConfirmModal
        openModal={openConfirmModal}
        handleCloseModal={handleCloseConfirmRemove}
        handleConfirm={onSubmitDelete}
      >
        <p>¿ Remover entidad {entityForm.name} ?</p>
      </ConfirmModal>

      <DataTable
        columns={columns}
        rows={entitiesList}
        handleDoubleClick={handleDoubleClickEntity}
        loading={loadingList}
      />

    </>
  );
}
